import { CITY_COUNTRY, COUNTRIES, COUNTRY_NAMES, type Country } from "./constants";

interface ParsedAddress {
  city?: string;
  country?: string;
  postal?: string;
  street?: string;
  houseNumber?: string;
}

const POSTAL = "\\d{4}(?:\\s?[A-Za-z]{2})?|\\d{5}";
const POSTAL_AT_START = new RegExp(`^(${POSTAL})\\s+(.+)$`);
const STREET_THEN_POSTAL = new RegExp(`^(.+?),\\s*(${POSTAL})\\s+(.+)$`);
const HOUSE_AT_END = /^(.+?)\s+(\d+[A-Za-z]?(?:\s?[-/]\s?\d+[A-Za-z]?)?)$/;

export class AddressLineMapper {
  constructor(private readonly countries = new CountryCodeMapper()) {}

  parse(raw: string): ParsedAddress {
    const trimmed = raw.trim().replace(/\s+/g, " ");
    if (!trimmed) return {};

    const withCountry = this.splitCountry(trimmed);
    const rest = withCountry.rest;
    const country = withCountry.country;

    const withStreet = this.streetLocality(rest, country);
    if (withStreet) return withStreet;

    const postalLead = rest.match(POSTAL_AT_START);
    if (postalLead) {
      const city = this.titleWords(postalLead[2]);
      return {
        postal: this.normalizePostal(postalLead[1]),
        city,
        country: country ?? this.countryForCity(city),
      };
    }

    const known = this.knownCity(rest);
    if (known) {
      return {
        city: known.city,
        country: country ?? known.cc,
        postal: this.embeddedPostal(rest),
      };
    }

    if (!rest) return country ? { country } : {};
    return { city: this.titleWords(rest), country };
  }

  private streetLocality(rest: string, country?: string): ParsedAddress | undefined {
    const postalLine = rest.match(STREET_THEN_POSTAL);
    if (postalLine) {
      const city = this.titleWords(postalLine[3]);
      return {
        ...this.streetAndHouse(postalLine[1]),
        postal: this.normalizePostal(postalLine[2]),
        city,
        country: country ?? this.countryForCity(city),
      };
    }

    const parts = rest.split(",").map((part) => part.trim()).filter(Boolean);
    if (parts.length < 2) return undefined;
    const streetPart = parts[0];
    const locality = parts.slice(1).join(", ");
    const known = this.knownCity(locality);
    if (!known && !this.embeddedPostal(locality)) return undefined;
    const city = known?.city ?? this.titleWords(locality.replace(/\b(\d{4}(?:\s?[A-Za-z]{2})?|\d{5})\b/, "").trim());
    return {
      ...this.streetAndHouse(streetPart),
      city,
      postal: this.embeddedPostal(locality),
      country: country ?? known?.cc ?? this.countryForCity(city),
    };
  }

  private streetAndHouse(raw: string): Pick<ParsedAddress, "street" | "houseNumber"> {
    const text = raw.trim();
    const numbered = text.match(HOUSE_AT_END);
    if (numbered) {
      return { street: this.titleWords(numbered[1]), houseNumber: numbered[2].replace(/\s+/g, "") };
    }
    return text ? { street: this.titleWords(text) } : {};
  }

  private splitCountry(text: string): { rest: string; country?: string } {
    const parts = text.split(",").map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 2) {
      const last = parts[parts.length - 1];
      const country = this.countries.toCode(last);
      if (country) {
        return { rest: parts.slice(0, -1).join(", "), country };
      }
    }

    const names = Object.keys(COUNTRY_NAMES).sort((a, b) => b.length - a.length);
    const lower = text.toLowerCase();
    for (const name of names) {
      if (lower.endsWith(name)) {
        const rest = text.slice(0, text.length - name.length).trim().replace(/[,\s]+$/, "");
        return { rest, country: COUNTRY_NAMES[name] };
      }
    }
    return { rest: text };
  }

  private knownCity(text: string): { city: string; cc: string } | undefined {
    const lower = text.toLowerCase();
    let best: { city: string; cc: string; len: number } | undefined;
    for (const name in CITY_COUNTRY) {
      if (lower.includes(name) && (!best || name.length > best.len)) {
        best = {
          city: this.titleWords(name),
          cc: CITY_COUNTRY[name],
          len: name.length,
        };
      }
    }
    return best;
  }

  private countryForCity(city: string): string | undefined {
    return CITY_COUNTRY[city.toLowerCase()];
  }

  private embeddedPostal(text: string): string | undefined {
    const match = text.match(/\b(\d{4}(?:\s?[A-Za-z]{2})?|\d{5})\b/);
    return match ? this.normalizePostal(match[1]) : undefined;
  }

  private normalizePostal(value: string): string {
    return value.replace(/\s+/g, " ").trim().toUpperCase();
  }

  private titleWords(value: string): string {
    return value
      .trim()
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }
}

export class CountryCodeMapper {
  toCode(value?: string | null): string {
    const raw = value?.trim() ?? "";
    if (!raw) return "";
    const byCode = COUNTRIES.find((c) => c.c.toLowerCase() === raw.toLowerCase());
    if (byCode) return byCode.c;
    return COUNTRY_NAMES[raw.toLowerCase()] ?? "";
  }
}

export class DepartmentCountryCatalog {
  constructor(
    private readonly allowed: Country[] = COUNTRIES,
    private readonly codes = new CountryCodeMapper(),
  ) {}

  static fromCodes(codes: string[] | undefined): DepartmentCountryCatalog {
    if (!codes || codes.length === 0) return new DepartmentCountryCatalog();
    const wanted = new Set(codes);
    const listed = COUNTRIES.filter((country) => wanted.has(country.c));
    if (listed.length === 0) return new DepartmentCountryCatalog();
    return new DepartmentCountryCatalog(listed);
  }

  list(): Country[] {
    return this.allowed;
  }

  toCode(value?: string | null): string {
    const code = this.codes.toCode(value);
    if (!code) return "";
    return this.allowed.some((country) => country.c === code) ? code : "";
  }
}

export class LocalizedCountryResolver {
  constructor(
    private readonly catalog: DepartmentCountryCatalog,
    private readonly nameOf: (code: string) => string,
  ) {}

  toCode(value?: string | null): string {
    const known = this.catalog.toCode(value);
    if (known) return known;
    const query = value?.trim().toLowerCase() ?? "";
    if (!query) return "";
    const match = this.catalog.list().find((country) => this.nameOf(country.c).trim().toLowerCase() === query);
    return match?.c ?? "";
  }
}
